import * as ngCommon from '@angular/common'
import * as ngCore from '@angular/core'
import type * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../state'
import { Store } from '../../store'

/**
 * Lets a user manage the API keys they can call the REST API with, for the
 * zone they are currently in.
 *
 * __NOTE:__ a newly generated key's plaintext lives only in
 * {@link generatedSg}, for as long as this component is open. It is never put
 * into the shared store, never re-fetched, and cannot be recovered once
 * dismissed — which is why dismissing it takes an explicit acknowledgement
 */
@ngCore.Component({
  imports: [
    ngCommon.DatePipe,
    ds.overlay.slideout.Component,
    ds.overlay.confirmation.Component,
    ds.button.Component,
    ds.input.text.Component,
    ds.input.datepicker.Component,
    ds.tooltip.Directive,
  ],
  selector: 'atn-api-keys-slideout',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.apiKeys.ApiKeys} */
  public readonly apiKeys = ngCore.inject(state.apiKeys.ApiKeys)

  /** @see {@link Store} */
  public readonly store = ngCore.inject(Store)

  /** Emitted when the user dismisses the slideout */
  public readonly close = ngCore.output()

  /** Name typed into the generate form */
  public readonly nameSg = ngCore.signal('')

  /**
   * Optional expiry date chosen in the generate form.
   *
   * The picker yields local midnight, which would expire the key at the *start*
   * of the chosen day — so {@link generate} pushes it to the end of that day,
   * which is what "expires on the 30th" reads as
   */
  public readonly expiresOnSg = ngCore.signal<Date | undefined>(undefined)

  /** Earliest date worth offering — an expiry in the past is never useful */
  public readonly minExpiryDate = new Date()

  /** Whether the generate form is expanded */
  public readonly generatingSg = ngCore.signal(false)

  /**
   * The one and only copy of a freshly minted key.
   *
   * Held here, in component state, so that it disappears with the component
   */
  public readonly generatedSg = ngCore.signal<
    ents.core.admin.ApiKey.Generated | undefined
  >(undefined)

  /** Set once the user has copied the new key, to soften the warning */
  public readonly copiedSg = ngCore.signal(false)

  /**
   * Set when the browser refused clipboard access, so the user is told to
   * select the key manually rather than assuming it was copied
   */
  public readonly copyFailedSg = ngCore.signal(false)

  /** Key the user has asked to revoke, pending confirmation */
  public readonly revokingSg = ngCore.signal<
    ents.core.admin.ApiKey | undefined
  >(undefined)

  /**
   * Whether the user is being asked to confirm closing while a generated key
   * is still unacknowledged
   */
  public readonly confirmingCloseSg = ngCore.signal(false)

  /**
   * A name is required, so the owner can tell their keys apart later, and an
   * expiry already in the past would mint a key that is dead on arrival
   */
  public readonly canSubmitSg = ngCore.computed(
    () =>
      this.nameSg().trim().length > 0 &&
      !this.apiKeys.loadingSg() &&
      !this.expiryIsPastSg(),
  )

  /** Whether the chosen expiry has already passed */
  public readonly expiryIsPastSg = ngCore.computed(() => {
    const expiresOn = this.expiresOnSg()
    return expiresOn !== undefined && _endOfDay(expiresOn) <= Date.now()
  })

  constructor() {
    this.apiKeys.load()
  }

  /** Mints the key and switches the panel over to revealing it */
  public async generate(): Promise<void> {
    if (!this.canSubmitSg()) {
      return
    }

    const expiresOn = this.expiresOnSg()
    const generated = await this.apiKeys.generate({
      expiresAt: expiresOn === undefined ? undefined : _endOfDay(expiresOn),
      name: this.nameSg().trim(),
    })

    if (generated) {
      this.generatedSg.set(generated)
      this.generatingSg.set(false)
      this.nameSg.set('')
      this.expiresOnSg.set(undefined)
    }
  }

  /**
   * Copies the freshly minted key to the clipboard.
   *
   * A blocked clipboard must not pass silently here the way it can elsewhere:
   * this is the user's only chance to capture the key, so a failure has to say
   * so rather than leave them believing it was copied
   */
  public copyGenerated(): void {
    const generated = this.generatedSg()
    if (!generated) {
      return
    }

    void navigator.clipboard
      .writeText(generated.key)
      .then(() => {
        this.copiedSg.set(true)
      })
      .catch(() => {
        this.copyFailedSg.set(true)
      })
  }

  /**
   * Handles a close request.
   *
   * Closing destroys the component and with it the only copy of a freshly
   * generated key, so an unacknowledged key turns this into a confirmation
   * rather than an immediate close
   */
  public requestClose(): void {
    if (this.generatedSg()) {
      this.confirmingCloseSg.set(true)
      return
    }

    this.close.emit()
  }

  /** Closes after the user accepted losing the unsaved key */
  public confirmClose(): void {
    this.confirmingCloseSg.set(false)
    this.close.emit()
  }

  /**
   * Drops the plaintext key. There is deliberately no way back to it — the
   * template makes that clear before offering this
   */
  public dismissGenerated(): void {
    this.generatedSg.set(undefined)
    this.copiedSg.set(false)
    this.copyFailedSg.set(false)
  }

  /** Revokes the key the user confirmed */
  public async confirmRevoke(): Promise<void> {
    const key = this.revokingSg()
    this.revokingSg.set(undefined)
    if (key) {
      await this.apiKeys.revoke(key.id)
    }
  }

  /**
   * Describes a key's current state, so an expired key does not read as
   * usable just because it was never explicitly revoked
   */
  public describeStatus(key: ents.core.admin.ApiKey): Status {
    if (!key.enabled) {
      return 'Revoked'
    }
    if (key.expiresAt !== undefined && key.expiresAt <= Date.now()) {
      return 'Expired'
    }
    return 'Active'
  }
}

/** The states a key can be in, as shown on its row */
type Status = 'Active' | 'Expired' | 'Revoked'

/**
 * Moves a picked calendar date to the last instant of that day.
 *
 * The picker hands back local midnight. Using that as-is would expire a key at
 * the *start* of the chosen day, a day earlier than the user meant
 */
function _endOfDay(date: Date): number {
  const endOfDay = new Date(date)
  endOfDay.setHours(23, 59, 59, 999)
  return endOfDay.getTime()
}
